import {
  ATLAS_PANEL_ROUTE_IDS,
  PENDING_MIGRATION_FROM_DARKWING_ROUTES,
} from '@/components/navigation/constants';
import { routeOptions } from '@/components/navigation/left/constants';
import {
  buildDarkwingPanelUrl,
  buildDarkwingUrl as buildDarkwingUrlUtil,
} from '@/components/navigation/utils';

export type RouteDestination = 'gaia' | 'darkwing' | 'none';

export interface RouteResolution {
  destination: RouteDestination;
  url: string | null;
  path: string;
  routePath?: string;
  params?: {
    org: string;
    agent: string;
  };
  searchParams?: Record<string, string>;
}

/**
 * Determines whether a route should stay in Gaia or redirect to Darkwing.
 *
 * Priority order:
 * 1. ROUTE_FEATURE_FLAGS - if route has a feature flag, use its value
 * 2. PENDING_MIGRATION_FROM_DARKWING_ROUTES - always redirects to Darkwing
 * 3. Default: Gaia (routes without configuration go to Gaia)
 *
 * @param routeId - The route identifier
 * @param featureEnabledStatuses - Map of route IDs to their feature flag values from ROUTE_FEATURE_FLAGS
 */
export const determineRouteDestination = (
  routeId: string,
  featureEnabledStatuses: Record<string, boolean>
): RouteDestination => {
  // Routes with feature flags in ROUTE_FEATURE_FLAGS
  const hasFeatureFlagConfig = routeId in featureEnabledStatuses;
  if (hasFeatureFlagConfig) {
    return featureEnabledStatuses[routeId] ? 'gaia' : 'darkwing';
  }

  // Routes pending migration (no Gaia implementation yet)
  if (PENDING_MIGRATION_FROM_DARKWING_ROUTES.has(routeId)) {
    return 'darkwing';
  }

  // Default: Gaia
  return 'gaia';
};

/**
 * Builds a Gaia URL for internal navigation
 */
export const buildGaiaUrl = (
  orgUrlName: string,
  facilityUrlName: string,
  routePath: string
): string => {
  return `/${orgUrlName}/${facilityUrlName}/${routePath}`;
};

/**
 * Checks if a route is a panel route that requires deep linking
 */
export const isPanelRoute = (routeId: string): boolean => {
  return (ATLAS_PANEL_ROUTE_IDS as readonly string[]).includes(routeId);
};

/**
 * Builds a Darkwing URL for external navigation
 */
export const buildDarkwingUrl = (
  orgName: string,
  facilityName: string,
  routeId: string,
  searchParams?: Record<string, string>
): string => {
  // If it's a panel route, use the panel URL builder
  if (isPanelRoute(routeId)) {
    return buildDarkwingPanelUrl(orgName, facilityName, routeId, searchParams);
  }
  return buildDarkwingUrlUtil(orgName, facilityName, routeId, searchParams);
};

/**
 * Gets the path for a route from the route configuration
 */
export const getRoutePath = (routeId: string): string => {
  const routeConfig = routeOptions.find((r) => r.id === routeId);
  return routeConfig?.path || routeId;
};

/**
 * Resolves a complete route including destination and URL
 */
export const resolveRoute = (
  routeId: string,
  featureEnabledStatuses: Record<string, boolean>,
  orgUrlName?: string,
  facilityUrlName?: string,
  searchParams?: Record<string, string>
): RouteResolution => {
  const destination = determineRouteDestination(routeId, featureEnabledStatuses);
  const routePath = getRoutePath(routeId);

  let url: string | null = null;
  let tanstackRoutePath: string | undefined;
  let params: { org: string; agent: string } | undefined;

  if (destination === 'gaia' && orgUrlName && facilityUrlName) {
    // For Gaia routes, provide TanStack Router path and params
    tanstackRoutePath = `/$org/$agent/${routePath}`;
    params = { org: orgUrlName, agent: facilityUrlName };
    // Still build URL for backward compatibility
    url = buildGaiaUrl(orgUrlName, facilityUrlName, routePath);
  } else if (destination === 'darkwing' && orgUrlName && facilityUrlName) {
    // For Darkwing routes, just build the external URL
    url = buildDarkwingUrl(orgUrlName, facilityUrlName, routeId, searchParams);
  }

  return {
    destination,
    url,
    path: routePath,
    routePath: tanstackRoutePath,
    params,
  };
};
